/**
 * Character Data Migration Utility
 * [DATE]: November 19, 2025
 * [PURPOSE]: Migrate character data from dual storage pattern to canonical nested structure
 * [WHY]: Eliminate data desync risk between flat and nested properties
 *
 * CRITICAL ISSUE:
 * - Race stored in both `character.race` AND `character.basics.race`
 * - Class stored in `character.class` AND `character.classes[0].className`
 * - Background stored in both `character.background` AND `character.basics.background`
 * - Different components read from different locations = HIGH RISK of desync
 *
 * SOLUTION:
 * - Use nested `basics` structure as canonical for race/background
 * - Use `classes` array as canonical for class data
 * - Keep flat properties for backward compatibility during migration period
 * - Will be removed in v0.03 after stable deployment
 */

/**
 * Migrate character data to canonical structure
 *
 * @param {Object} character - Character object (may be old or new format)
 * @returns {Object} Migrated character with canonical structure
 */
export function migrateCharacterData(character) {
  if (!character) {
    console.warn('[MIGRATION] No character data to migrate');
    return character;
  }

  let migrated = false;
  const changes = [];

  // STEP 1: Ensure basics object exists
  if (!character.basics) {
    character.basics = {};
    migrated = true;
    changes.push('Created basics object');
  }

  // STEP 2: Migrate race (flat → nested)
  if (character.race && !character.basics.race) {
    character.basics.race = character.race;
    migrated = true;
    changes.push(`Migrated race: "${character.race}" → basics.race`);
  }

  if (character.subrace && !character.basics.subrace) {
    character.basics.subrace = character.subrace;
    migrated = true;
    changes.push(`Migrated subrace: "${character.subrace}" → basics.subrace`);
  }

  // STEP 3: Migrate background (flat → nested)
  if (character.background && !character.basics.background) {
    character.basics.background = character.background;
    migrated = true;
    changes.push(`Migrated background: "${character.background}" → basics.background`);
  }

  // STEP 4: Migrate alignment (if flat)
  if (character.alignment && !character.basics.alignment) {
    character.basics.alignment = character.alignment;
    migrated = true;
    changes.push('Migrated alignment');
  }

  // STEP 5: Ensure classes array exists
  if (!character.classes || character.classes.length === 0) {
    if (character.class) {
      // Convert single class to classes array
      const className = character.class;
      const level = character.level || 1;
      const subclass = character.subclass || '';

      // [v1.0]: Use contentResolver for hit dice — supports custom classes
      const { getHitDice: resolveHitDice } = require('../utils/contentResolver');
      const getHitDice = (className) => {
        // Try contentResolver first (SRD + custom sources)
        const resolved = resolveHitDice(className);
        if (resolved) return resolved;
        // Fallback for edge cases where sources aren't loaded yet
        const hitDiceMap = {
          'Barbarian': '1d12',
          'Fighter': '1d10', 'Paladin': '1d10', 'Ranger': '1d10',
          'Bard': '1d8', 'Cleric': '1d8', 'Druid': '1d8', 'Monk': '1d8', 'Rogue': '1d8', 'Warlock': '1d8',
          'Sorcerer': '1d6', 'Wizard': '1d6'
        };
        return hitDiceMap[className] || '1d8';
      };

      character.classes = [{
        className: className,
        level: level,
        subclass: subclass,
        subclassData: character.subclassData || {},
        hitDice: getHitDice(className),
        spellbook: character.wizardSpells || character.spellbook || []
      }];

      migrated = true;
      changes.push(`Migrated class: "${className}" → classes[0]`);
    } else {
      // No class data at all - create empty array
      character.classes = [];
      changes.push('Created empty classes array');
    }
  }

  // STEP 6: Compute total level and primaryClass
  if (character.classes && character.classes.length > 0) {
    // Calculate total level
    const totalLevel = character.classes.reduce((sum, cls) => sum + (cls.level || 0), 0);

    if (character.level !== totalLevel) {
      character.level = totalLevel;
      changes.push(`Updated total level: ${totalLevel}`);
    }

    // Find highest level class as primary
    const primaryClassEntry = character.classes.reduce((prev, curr) =>
      (curr.level || 0) > (prev.level || 0) ? curr : prev
    );

    const primaryClass = primaryClassEntry.className;
    if (character.primaryClass !== primaryClass) {
      character.primaryClass = primaryClass;
      changes.push(`Set primaryClass: "${primaryClass}"`);
    }
  }

  // STEP 7: Mark legacy properties for deprecation tracking
  // Keep them for backward compatibility but mark for removal in v0.03
  if (character.race || character.class || character.background) {
    character._legacyProperties = {
      race: character.race || null,
      class: character.class || null,
      background: character.background || null,
      subrace: character.subrace || null,
      _deprecationNotice: 'These properties will be removed in v0.03. Use basics.race, classes array, and basics.background instead.'
    };
  }

  return character;
}

/**
 * Validate character data structure
 * [PURPOSE]: Catch data integrity issues early
 * [WHY]: Prevent desync bugs and data loss
 *
 * @param {Object} character - Character object to validate
 * @returns {Object} Validation result {valid: boolean, errors: string[], warnings: string[]}
 */
export function validateCharacterData(character) {
  if (!character) {
    return { valid: false, errors: ['Character object is null or undefined'], warnings: [] };
  }

  const errors = [];
  const warnings = [];

  // VALIDATION 1: Check basics structure
  if (!character.basics) {
    errors.push('Missing basics object');
  } else {
    if (!character.basics.race) {
      errors.push('Missing basics.race');
    }
    if (!character.basics.background) {
      warnings.push('Missing basics.background (optional for new characters)');
    }
  }

  // VALIDATION 2: Check classes array
  if (!Array.isArray(character.classes)) {
    errors.push('classes is not an array');
  } else if (character.classes.length === 0) {
    warnings.push('classes array is empty (expected for new characters)');
  } else {
    character.classes.forEach((cls, idx) => {
      if (!cls.className) {
        errors.push(`classes[${idx}] missing className`);
      }
      if (typeof cls.level !== 'number' || cls.level < 1) {
        errors.push(`classes[${idx}] has invalid level: ${cls.level}`);
      }
      if (!cls.hitDice) {
        warnings.push(`classes[${idx}] missing hitDice (will use default)`);
      }
    });
  }

  // VALIDATION 3: Check for desync (CRITICAL)
  if (character.race && character.basics?.race && character.race !== character.basics.race) {
    errors.push(`🔴 DESYNC DETECTED: race="${character.race}" but basics.race="${character.basics.race}"`);
  }

  if (character.class && character.classes?.[0]?.className && character.class !== character.classes[0].className) {
    errors.push(`🔴 DESYNC DETECTED: class="${character.class}" but classes[0].className="${character.classes[0].className}"`);
  }

  if (character.background && character.basics?.background && character.background !== character.basics.background) {
    errors.push(`🔴 DESYNC DETECTED: background="${character.background}" but basics.background="${character.basics.background}"`);
  }

  // VALIDATION 4: Check level consistency
  if (character.classes && character.classes.length > 0) {
    const calculatedLevel = character.classes.reduce((sum, cls) => sum + (cls.level || 0), 0);
    if (character.level && character.level !== calculatedLevel) {
      warnings.push(`Level mismatch: character.level=${character.level} but sum of class levels=${calculatedLevel}`);
    }
  }

  // VALIDATION 5: Deprecation warnings
  // [REMOVED APR 2026 Phase 3A.2]: The deprecation warning fired on every autosave tick
  // (once every 2s), flooding the console. Flat properties (race/class/background) are
  // kept for backward compatibility with older saves; a sync effect in App.jsx mirrors
  // them into basics.*. The warning was noise, not actionable — silent compatibility
  // is the correct behavior until v0.03 when flat props are removed via migration.

  // Log results
  if (errors.length > 0) {
    console.error('[VALIDATION] Character data validation FAILED:', {
      errors,
      warnings,
      character: {
        basics: character.basics,
        classes: character.classes,
        legacyProperties: {
          race: character.race,
          class: character.class,
          background: character.background
        }
      }
    });
  } else if (warnings.length > 0) {
    console.warn('[VALIDATION] Character data validation passed with warnings:', warnings);
  }

  return {
    valid: errors.length === 0,
    errors,
    warnings
  };
}

/**
 * Sanitize character data before save
 * [PURPOSE]: Ensure data is in correct format before persisting
 * [WHY]: Prevent corrupted save files
 *
 * @param {Object} character - Character object to sanitize
 * @returns {Object} Sanitized character ready for save
 */
export function sanitizeCharacterData(character) {
  if (!character) return character;

  // Ensure basics exists
  if (!character.basics) {
    character.basics = {};
  }

  // Ensure classes is an array
  if (!Array.isArray(character.classes)) {
    character.classes = [];
  }

  // Remove any temporary UI state
  delete character._tempState;
  delete character._uiState;

  // Ensure numeric types
  if (character.level) character.level = Number(character.level);
  if (character.strength) character.strength = Number(character.strength);
  if (character.dexterity) character.dexterity = Number(character.dexterity);
  if (character.constitution) character.constitution = Number(character.constitution);
  if (character.intelligence) character.intelligence = Number(character.intelligence);
  if (character.wisdom) character.wisdom = Number(character.wisdom);
  if (character.charisma) character.charisma = Number(character.charisma);

  return character;
}
